/**
 * Maps `data-artifact-*` stream events from `useChat({ onData })` into the
 * artifact store. Returns `true` when the event was consumed so the chat's
 * onData callback can early-return and skip its other branches.
 *
 * The protocol is intentionally narrow — see `@taqat/core/types/artifact.ts`
 * for the canonical wire format and the reasoning behind each event.
 *
 * @module lib/artifacts/data-stream-handler
 */

import { ARTIFACT_STREAM_TYPES, type ArtifactKind } from "@taqat/core/types";
import { useArtifactStore } from "@/store/artifact-store";

/**
 * Loose shape of the data part received by `onData`. The AI SDK's typed
 * `UIDataTypes` union is too narrow for our custom events, so we read fields
 * defensively here and only commit known shapes to the store.
 */
type StreamPart = { type?: string; data?: unknown } | null | undefined;

/**
 * Handles one stream part and forwards it to the artifact store.
 * Caller is `useChat({ onData: (part) => { ... } })`.
 */
export function handleArtifactStreamEvent(part: StreamPart): boolean {
  if (!part || typeof part.type !== "string") return false;

  switch (part.type) {
    case ARTIFACT_STREAM_TYPES.INIT: {
      const init = part.data as
        | { id: string; title: string; kind: ArtifactKind; mode: "create" | "update" }
        | undefined;
      if (!init || typeof init.id !== "string" || typeof init.title !== "string") {
        return true; // event recognized but malformed — swallow it
      }
      const store = useArtifactStore.getState();
      if (init.mode === "update") {
        store.beginUpdate({ id: init.id, title: init.title, kind: init.kind });
      } else {
        store.beginCreate({ id: init.id, title: init.title, kind: init.kind });
      }
      return true;
    }

    case ARTIFACT_STREAM_TYPES.DELTA: {
      // New shape: { id, chunk }. Tolerate the legacy bare-string shape so a
      // client running ahead of the server doesn't drop deltas.
      const d = part.data as { id?: string; chunk?: string } | string | undefined;
      const id = typeof d === "object" && d ? (d.id ?? "") : "";
      const chunk =
        typeof d === "string" ? d : typeof d?.chunk === "string" ? d.chunk : "";
      if (chunk.length > 0) {
        useArtifactStore.getState().appendDelta(id, chunk);
      }
      return true;
    }

    case ARTIFACT_STREAM_TYPES.SNAPSHOT: {
      // The payload is a real JSON value here, not text — re-stringify once
      // for storage in the store's content:string field, matching what every
      // downstream reader (parseImageArtifactContent, the DB column,
      // ArtifactToolResult.content) already expects.
      const s = part.data as { id?: string; data?: unknown } | undefined;
      if (s && typeof s.id === "string" && s.data !== undefined) {
        useArtifactStore.getState().setSnapshotContent(s.id, JSON.stringify(s.data));
      }
      return true;
    }

    case ARTIFACT_STREAM_TYPES.REPLACE: {
      // Text body, already final — unlike SNAPSHOT it is not stringified, and
      // unlike DELTA it replaces rather than appends. Sent when the server
      // rewrote a body the client has already rendered (a repaired slide deck),
      // so that what is on screen matches what was persisted.
      const r = part.data as { id?: string; content?: string } | undefined;
      if (r && typeof r.id === "string" && typeof r.content === "string") {
        useArtifactStore.getState().replaceContent(r.id, r.content);
      }
      return true;
    }

    case ARTIFACT_STREAM_TYPES.SLIDE_PLAN: {
      const plan = part.data as { order?: string[]; editing?: string[] } | undefined;
      if (plan && Array.isArray(plan.order)) {
        useArtifactStore.getState().beginSlidePlan({
          order: plan.order,
          editing: Array.isArray(plan.editing) ? plan.editing : [],
        });
      }
      return true;
    }

    case ARTIFACT_STREAM_TYPES.SLIDE_DELTA: {
      const d = part.data as { slideId?: string; chunk?: string } | undefined;
      if (d && typeof d.slideId === "string" && typeof d.chunk === "string") {
        useArtifactStore.getState().appendSlideDelta({ slideId: d.slideId, chunk: d.chunk });
      }
      return true;
    }

    case ARTIFACT_STREAM_TYPES.SLIDE_FINISH: {
      const d = part.data as { slideId?: string; html?: string } | undefined;
      if (d && typeof d.slideId === "string" && typeof d.html === "string") {
        useArtifactStore.getState().finishSlide({ slideId: d.slideId, html: d.html });
      }
      return true;
    }

    case ARTIFACT_STREAM_TYPES.FINISH: {
      useArtifactStore.getState().finishStream();
      return true;
    }

    case ARTIFACT_STREAM_TYPES.ERROR: {
      const payload = part.data as
        | { id?: string; message?: string; instructions?: string }
        | undefined;
      useArtifactStore.getState().failStream({
        message: payload?.message || "The update could not be completed. Please try again.",
        instructions: payload?.instructions,
      });
      return true;
    }

    default:
      return false;
  }
}
